<!--
  Created 11.10.2023
  Entity card component for the proposal show page.
  This component showcases specific entities on the proposal page.
-->
<template>
  <article
    class="isolate flex max-h-[90vh] flex-1 flex-col overflow-hidden border border-gray-200
      lg:flex-row print:mt-8 print:h-fit print:!bg-gray-100 print:first-of-type:mt-0"
    :class="[
      {'lg:flex-col':isVertical},
      hasWhiteBackground ? 'bg-gray-100' : 'bg-white',
      {'h-full':isSlide},
      {'rounded-md':isRounded},
    ]">
    <div
      class="group relative shrink-0 basis-1/2"
      :class="{'max-h-[40vh] !basis-1/3':isVertical}">
      <MediaFigure
        v-if="image"
        :image-alt="image.custom_properties?.alt_description"
        :fig-caption="image.name"
        class="max-h-[40vh] print:aspect-[3/1]"
        :image="image" />
      <div
        v-if="contentEditable && image"
        class="absolute inset-0 mt-36 w-full p-4">
        <div class="mx-auto max-w-7xl">
          <media-select-single
            :selected="image"
            :settings="{
              proposal: {
                name: 'Current Proposal',
                get: route('api.proposal.media.index', {proposal: proposal.id}),
                upload: route('api.proposal.media.store', {proposal: proposal.id})
              },
              entity: {
                name: 'Original entity',
                get: route('api.entity.media.index', {placeEntity: entity.place_entity_id}),
                upload: route('api.proposal.media.store', {proposal: proposal.id})
              }
            }"
            :default_search="(entity.place
              ? (entity.place?.name ?? entity.label ?? entity.name) + (entity.destination ? (', ' + entity.destination.name + ', ' + entity.destination.parent?.name) : '')
              : entity.place_entity?.place
                ? (entity.place_entity?.place?.name ?? entity.label ?? entity.name)
                  + (entity.place_entity?.parent?.name ? ', ' + entity.place_entity?.parent?.name : '')
                  + (entity.place_entity?.parent?.parent?.name ? ', ' + entity.place_entity?.parent?.parent?.name : '')
                : (entity.label || entity.name)
                  ? (entity.label ?? entity.name) + (entity.parent?.name ? ', ' + entity.parent?.name : '')
                  : entity.texts[currentLanguage]?.name)"
            :cropable="typeof image !== 'string'"
            :coordinates="imageCrop ? imageCrop.coordinates : null"
            hide-media-update-buttons
            @update="updateMedia"
            @crop="cropped" />
        </div>
      </div>
    </div>
    <div
      class="relative flex basis-1/2 flex-col justify-center overflow-hidden p-4 pb-6 text-left lg:p-12"
      :class="[
        {'lg:px-12 lg:py-8':isVertical},
        {'lg:px-6 lg:py-4':isCompact},
        {'!justify-start':isGrouped},
        {'!basis-2/3':isVertical}
      ]">
      <!-- Settings button -->
      <Tooltip
        v-if="contentEditable && !hideEntitySettingsButton"
        class="!absolute right-4 top-4 z-20"
        align="right"
        :class="{'pointer-events-none':!proposal.data.sections.lodgings.visible}"
        content-class="ml-4 mt-2 bg-gray-900 text-gray-100 w-56 p-2 rounded opacity-95 translate-x-[calc(-100%+0.8rem)]">
        <template #trigger>
          <AdjustmentsHorizontalIcon
            class="size-7 rounded-md text-gray-400 drop-shadow-sm hover:text-gray-500"
            aria-hidden="true"
            @click="contentEditable ? setOpenEntityItinerarySettings(entity.id) : false" />
        </template>
        <template #content>
          <p class="text-xs">
            Click to edit itinerary entity settings.
          </p>
        </template>
      </Tooltip>
      <!-- entity info -->
      <small
        v-if="entity.type === 'lodging' && proposal.destinations.length > 1"
        class="cursor-default text-gray-500">{{ entities[entitiesType][entityIndex]?.place_entity?.parent?.name }}</small>
      <TextEditableTextarea
        v-if="contentEditable && entities[entitiesType][entityIndex]?.texts"
        :id="`entity-${entity.id}-name`"
        v-model="entities[entitiesType][entityIndex].texts[currentLanguage].name"
        class="w-full"
        background-style="gray"
        :text-area-class="'bg-transparent whitespace-pre-wrap text-2xl font-medium text-gray-800 ' +
          'placeholder:italic placeholder:text-gray-600 placeholder:opacity-50 cursor-text'"
        :placeholder="'entity name here..'"
        :show-buttons="false"
        :saved-changes="savedChanges"
        @edited="editEntityInProposal()" />
      <h1
        v-else
        class="text-2xl font-medium text-gray-800"
        :class="{'text-xl':isCompact}">
        {{ entity.texts[currentLanguage]?.name }}
      </h1>
      <!-- entity time, start and end -->
      <div class="relative mt-2 flex w-fit flex-row space-x-4 text-gray-700">
        <div
          class="flex items-center justify-center space-x-1">
          <Component
            :is="entity.type === 'lodging' ? ArrowDownCircleIcon : CalendarDaysIcon"
            class="inline-block size-5 text-cyan-700" />
          <small>{{ getDateTimeInDestinationTimezone(
            entity.start, entity.destination.timezone, 'ddd DD/MM [kl.] HH:mm'
          ) }}</small>
        </div>
        <div
          class="flex items-center justify-center space-x-1">
          <Component
            :is="entity.type === 'lodging' ? ArrowUpCircleIcon : MapPinIcon"
            class="inline-block size-5 text-cyan-700" />
          <small>{{ entity.type === 'lodging' ? getDateTimeInDestinationTimezone(
            entity.end ?? entity.start, entity.destination.timezone, 'ddd DD/MM [kl.] HH:mm'
          ) : entity.place_entity?.parent?.area ? (entity.place_entity?.parent?.name + ', ' + entity.place_entity?.parent?.area) : '' }}</small>
        </div>
      </div>
      <TextEditableTextarea
        v-if="contentEditable && entities[entitiesType][entityIndex]?.texts"
        :id="`entity-${entity.id}-description`"
        v-model="entities[entitiesType][entityIndex].texts[currentLanguage].description"
        :tab-rows="3"
        class="w-full"
        background-style="gray"
        :text-area-class="'bg-transparent whitespace-pre-wrap mt-4 whitespace-pre-wrap leading-snug ' +
          'placeholder:italic placeholder:text-gray-600 placeholder:opacity-50 cursor-text'"
        :placeholder="'entity description here..'"
        :show-buttons="false"
        :saved-changes="savedChanges"
        @edited="editEntityInProposal()" />
      <template v-else>
        <p
          v-if="entity.texts[currentLanguage]?.description"
          class="mt-4 overflow-y-auto whitespace-pre-wrap leading-snug"
          :class="{'text-sm':isCompact}">
          <template v-if="!hasNoStringLimit">
            <StringLimiter
              v-if="!store.printMode"
              :string="entity.texts[currentLanguage]?.description"
              :length="stringLimit"
              is-simple />
            <span v-else>
              {{ entity.texts[currentLanguage]?.description }}
            </span>
          </template>
          <template v-else>
            {{ entity.texts[currentLanguage]?.description }}
          </template>
        </p>
      </template>
      <a
        v-if="entity.cta"
        href="{{ entity.cta.url }}"
        class="text-sm text-gray-500 underline print:hidden">{{ entity.cta.text }}</a>
      <!--      <button
        v-if="entity.alternative"
        class="focus:ring-offset-3 -ml-4 mt-4 inline-flex w-fit items-center
            justify-center rounded-md border-2 border-transparent
            px-4 py-2 text-sm font-semibold
            text-cyan-700 underline hover:border-cyan-700 hover:text-cyan-800
            hover:no-underline hover:shadow-sm focus:border-cyan-700 focus:text-cyan-800 focus:no-underline focus:outline-none focus:ring-2 focus:ring-cyan-400"
        :class="{ 'cursor-not-allowed no-underline opacity-50 hover:border-transparent hover:shadow-none focus:ring-transparent': requested }"
        @click.prevent="requestAlternative(entity)">
        <span v-if="!requested">
          Send forespørsel
        </span>
        <span v-else>
          <CheckIcon class="mr-1 inline-block h-5 w-5" />
          Forespørsel sendt
        </span>
      </button>-->
    </div>
  </article>
</template>
<script setup>
/**
 * @component
 * Entity Card component.
 *
 * @param {object} entity - The entity object.
 * @param {boolean} isVertical - Indicates if the card remains vertically stacked on larger screens.
 * @param {boolean} isGrouped - Indicates if the card is grouped.
 *
 * @returns {object}
 *
 * @example
 * <EntityCard
 *  :entity="entity"
 *  is-vertical
 *  is-grouped />
 */

/* external */
import {
  ArrowDownCircleIcon, ArrowUpCircleIcon, CheckIcon, CalendarDaysIcon, MapPinIcon, ClockIcon, AdjustmentsHorizontalIcon,
} from '@heroicons/vue/24/outline';

/* local */
import {
  computed, onMounted, ref,
} from 'vue';
import StringLimiter from '@/Components/StringLimiter.vue';
import Cookies from 'js-cookie';
import {
  formatDateTimeToLocaleCustomString, formatTime, getDateTimeInDestinationTimezone,
} from '@/utils';
import MediaFigure from '@/Pages/Proposal/Components/MediaFigure.vue';
import TextEditableTextarea from '@/Components/Forms/TextEditableTextarea.vue';
import { useProposalStore } from '@/Stores/Proposal';
import { notify } from 'notiwind';
import MediaSelectSingle from '@/Components/Media/MediaPickerSingle.vue';
import Tooltip from '@/Components/Tooltip.vue';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */

const props = defineProps({
  entity: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  entitiesType: {
    type: String,
    required: true,
    default: '',
  },
  entityIndex: {
    type: Number,
    required: false,
    default: 0,
  },
  contentEditable: {
    type: Boolean,
    required: false,
    default: false,
  },
  stringLimit: {
    type: Number,
    required: false,
    default: 150,
  },
  hasNoStringLimit: {
    type: Boolean,
    required: false,
    default: false,
  },
  /**
   * Hides the top-right settings button on the card.
   */
  hideEntitySettingsButton: {
    type: Boolean,
    required: false,
    default: false,
  },
  /**
   * Indicates if the card remains vertically stacked on larger screens.
   * Used to keep vertical layout on larger screens. Default is `false`.
   */
  isVertical: {
    type: Boolean,
    required: false,
    default: false,
  },
  /**
   * Indicates if the card is grouped.
   * Used to top-align the lead texts. Default is `false`.
   */
  isGrouped: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasWhiteBackground: {
    type: Boolean,
    required: false,
    default: false,
  },
  isSlide: {
    type: Boolean,
    required: false,
    default: false,
  },
  isRounded: {
    type: Boolean,
    required: false,
    default: false,
  },
  isCompact: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const requested = ref(false);
const currentLanguage = ref(Cookies.get('currentLanguage') ?? 'no');

const entity = computed(() => props.entity);
const entitiesType = computed(() => props.entitiesType);
const entityIndex = computed(() => props.entityIndex);

const store = useProposalStore();
const proposal = computed(() => store.proposal);
const entities = computed(() => store.entities);
const savedChanges = computed(() => !store.unsavedChanges.changes.itinerary);

const image = computed(() => entity.value.data?.image ?? entities.value[entitiesType?.value][entityIndex.value]?.data?.image ?? null);
const imageCrop = computed(() => entity.value.data?.media_crop ?? entities.value[entitiesType?.value][entityIndex.value]?.data?.media_crop ?? null);

/* *********************************************************************
 * Component functions
 * ****************************************************************** */

/**
 *
 * @param entity
 */
function requestAlternative(entity) {
  requested.value = true;
  console.log('Send forespørsel på: ', entity.title);
}

/**
 * Edit entity in proposal itinerary.
 * It uses the currently selected entity and updates it in the proposal itinerary.
 */
function editEntityInProposal() {
  store.unsavedChanges.changes.itinerary = true;

  axios.put(route('api.proposal.itinerary.update', {
    proposal: proposal.value.id, itinerary: props.entity.id,
  }), {
    ...entities.value[entitiesType.value][entityIndex.value],
  }).then(() => {
    store.unsavedChanges.changes.itinerary = false;
    // store.fetchEntities();
    notify({
      type: 'success',
      title: 'Saved',
      text: 'Entity updated!',
    }, 4000);
  }).catch((error) => {
    // eslint-disable-next-line no-console
    console.error(error);
  }).finally(() => {
    // rest in peace
  });
}

/**
 * Set open itinerary settings id
 * @param {number} entityId - Entity id
 */
function setOpenEntityItinerarySettings(entityId) {
  store.openEntityItinerarySettings = entityId;
}

/**
 * Update media function for media picker
 * @param {object} media - Media object
 */
function updateMedia(media) {
  store.entities[entitiesType.value][entityIndex.value].data.image = media;
  store.entities[entitiesType.value][entityIndex.value].data.media_id = media.id;
  editEntityInProposal();
}

/**
 * Update media crop function for media picker
 * @param {object} data - Media crop data
 */
function cropped(data) {
  store.entities[entitiesType.value][entityIndex.value].data.media_crop = data;
  editEntityInProposal();
}

/* *********************************************************************
 * Page lifecycle functions
 * ****************************************************************** */

</script>
